import { ToggleButton, ToggleButtonGroup, ToggleButtonGroupProps, Typography } from '@mui/material';
import { DurationUnit } from 'date-fns';
import { findIndex, isEqual } from 'lodash';
import { useMemo } from 'react';

export type Timeframe = [number, DurationUnit];

type TimeframeButtonGroupProps = {
    currentTimeframe: Timeframe;
    availableTimeframes: Timeframe[];
    onTimeframeChange: (newTimeframe: Timeframe) => void;
} & Pick<ToggleButtonGroupProps, 'sx'>;

export const TimeframeButtonGroup = ({
    currentTimeframe,
    availableTimeframes,
    onTimeframeChange,
    sx,
}: TimeframeButtonGroupProps) => {
    const currentTimeframeIndex = useMemo(() => {
        const idx = findIndex(availableTimeframes, (t) => isEqual(currentTimeframe, t));
        if (idx === -1) {
            console.warn('TimeframeButtonGroup: currentTimeframe not found in availableTimeframes');
        }
        return idx;
    }, [availableTimeframes, currentTimeframe]);
    return (
        <ToggleButtonGroup
            value={currentTimeframeIndex}
            exclusive
            size="small"
            sx={sx}
            onChange={(_, newTimeframeIndex) => {
                if (newTimeframeIndex === null) {
                    return;
                }

                onTimeframeChange(availableTimeframes[newTimeframeIndex]);
            }}
            aria-label="chart timeframe"
        >
            {availableTimeframes.map((timeframe, i) => {
                const [value, unit] = timeframe;
                const unitLabel = value === 1 ? unit.replace(/s$/, '') : unit;
                return (
                    <ToggleButton key={unit} value={i} aria-label={`${value} ${unitLabel}`}>
                        <Typography variant="button">
                            {value} {unitLabel}
                        </Typography>
                    </ToggleButton>
                );
            })}
        </ToggleButtonGroup>
    );
};
